import { Button, Skeleton } from "@mui/material";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { JSX } from "react";

import { useCan } from "../../../auth/permissions.js";
import { marketingApi } from "../client.js";
import type { AgentSettings } from "../types.js";

/**
 * Pause / resume control for the autonomous marketing agent. Mirrors
 * the Sales `AgentControlButton` so both autonomous agents share the
 * same operator chrome: red "Pause" while running, green "Resume"
 * while paused, pinned to the far right of the page header.
 *
 * Hidden entirely for non-`marketing:edit` viewers — they still see
 * draft activity in the queue, just no kill switch.
 */
export function MarketingAgentControl(): JSX.Element | null {
  const canEdit = useCan("marketing:edit");
  const qc = useQueryClient();

  const settings = useQuery({
    queryKey: ["marketing", "agent-settings"],
    queryFn: marketingApi.getAgentSettings,
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  });

  const patch = useMutation({
    mutationFn: marketingApi.patchAgentSettings,
    onSuccess: (next: AgentSettings) => {
      qc.setQueryData(["marketing", "agent-settings"], next);
    },
  });

  if (!canEdit) return null;

  // Loading skeleton — placeholder pill so the header doesn't shift.
  if (!settings.data) {
    return (
      <Skeleton
        variant="rounded"
        aria-label="Loading marketing agent control"
        sx={{ height: 28, width: 96, borderRadius: 1 }}
      />
    );
  }

  const paused = settings.data.paused;
  const busy = patch.isPending;

  return (
    <Button
      type="button"
      variant="contained"
      color={paused ? "success" : "error"}
      size="small"
      disableElevation
      disabled={busy}
      onClick={() => patch.mutate({ paused: !paused })}
      startIcon={
        paused ? <PlayArrowIcon sx={{ fontSize: 16 }} /> : <PauseIcon sx={{ fontSize: 16 }} />
      }
      title={
        paused
          ? "Resume — marketing agent will tick again on its next 30 s cycle"
          : "Pause — stops new drafts / publish loops; in-flight runs finish"
      }
      aria-label={paused ? "Resume marketing agent" : "Pause marketing agent"}
      // Match the Sales AgentControlButton exactly (contained, px:1.5 / py:0.75).
      sx={{ fontSize: 12, fontWeight: 600, px: 1.5, py: 0.75 }}
    >
      {paused ? "Resume agent" : "Pause agent"}
    </Button>
  );
}
